import Repository from './Repository';

import { vue } from '../main';


const baseUrl = process.env.VUE_APP_API_URL;
const base = `${baseUrl}/instance`;

export default {

  /**
     * Returns a list of instances in the active channel.
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
  getAll() {
    return Repository.get(`${base}/list`);
  },

  /**
     * Acquires the selected Instance.
     * All other instance related actions (add member, add chapters,...) will be
     * related to this instance until the instance is released.
     *
     * @param payload
     * @returns {Promise<AxiosResponse<T>>}
     */
  acquire(payload) {
    return Repository.post(`${base}/acquire`, {
      id: payload.id, // Instance ID
      force: payload.force, // Boolean
      readonly: payload.readonly, // Boolean
    });
  },

  /**
     * Releases the currently acquired instance. The instance data will not
     * be saved. Progress made since the last Perm-DB save is lost.
     *
     * @returns {Promise<AxiosResponse<T> | never>}
     */
  release() {
    return Repository.get(`${base}/release`)
      .then(response => response)
      .catch((error) => {
        // if "ERROR_INSTANCE_NOT_USED" is returned -> Remove the active Instance from the Store
        if (error.response.status === 400) {
          if (error.response.data.error === vue.getErrorCodeByKey('ERROR_INSTANCE_NOT_USED')) {
            vue.$store.dispatch('setInstanceData', null);
            this.$router.push({ name: 'home' });
          }
        }
        return Promise.reject(error);
      });
  },

  /**
     * Returns the data of the currently active instance.
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
  active() {
    return Repository.get(`${base}/active`);
  },

  /**
     * Re-fetches the currently active instance and pushes its information
     * to the store.
     *
     * @param notify
     * @returns {Promise<T>}
     */
  async refetchActive(notify = true) {
    return await this.active()
      .then((response) => {
        // Only if there is an active Instance
        if (response.data.success) {
          vue.$store.dispatch('setInstanceData', response.data);

          if (notify) {
            vue.$snotify.info(vue.$t('UI_INSTANCE_ACTIVE_LOADED', { instance: response.data.title }));
          }

          /*
                     * If the Instance is in Live Mode
                     */
          if (response.data.live) {
            if (response.data.live.isRunning) {
              vue.$store.dispatch('updateInstanceLiveSeconds', response.data.live.seconds_elapsed);
            }
          }
        }
        return Promise.resolve(response);
      })
      .catch((error) => {
        if (error.response.data.error) {
          /**
                     * This error happens when someone forces entry to an instance
                     * while another user acquired it.
                     */
          if (error.response.data.error === vue.getErrorCodeByKey('ERROR_INSTANCE_OWNED_BY_OTHER')) {
            vue.$store.dispatch('setInstanceData', null);
            vue.$router.push({ name: 'home' });
          }
        }
        Promise.reject(error);
      });
  },

  /**
     * Renames the given instance
     *
     * @param payload
     * @returns {Promise<AxiosResponse<T>>}
     */
  rename(payload) {
    return Repository.post(`${base}/rename`, {
      id: payload.id,
      title: payload.title,
    });
  },

  /**
     * Creates a new instance.
     *
     * @param payload
     * @returns {Promise<AxiosResponse<T>>}
     */
  create(payload) {
    return Repository.post(`${base}/create`, {
      title: payload.title,
      acquire: payload.acquire,
      target: payload.target,
    });
  },

  /**
     * Deletes the given instance.
     *
     * @param payload
     * @returns {Promise<AxiosResponse<T>>}
     */
  delete(payload) {
    return Repository.post(`${base}/delete`, {
      id: payload.id,
    });
  },

  /**
     * Saves the currently active instance temporary data into the permanent
     * database.
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
  save() {
    return Repository.get(`${base}/save`);
  },

  /**
     * Moves the instance to another Channel.
     *
     * @param payload
     * @returns {Promise<AxiosResponse<T>>}
     */
  move(payload) {
    return Repository.post(`${base}/move`, {
      id: payload.instanceId,
      target: payload.targetChannelId,
    });
  },
};
